"use client";
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { signOutWithAudit } from "../../utils/audit";
import IntraHeader from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { formatProfileName } from "../../utils/profileName";

export default function MyProfile() {
  const mounted = useRef(true);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    mounted.current = true;
    loadProfile();

    return () => {
      mounted.current = false;
    };
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("Session error:", sessionError);
        if (mounted.current) {
          setErrorMessage("Failed to read login session.");
          setLoading(false);
        }
        return;
      }

      if (!session?.user) {
        if (mounted.current) {
          setErrorMessage("You are not logged in.");
          setLoading(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, full_name, name_prefix, roblox_name, discord_name, discord_id, email, created_at",
        )
        .eq("id", session.user.id)
        .maybeSingle();

      if (error) {
        console.error("Profile fetch error:", error);
        if (mounted.current) {
          setErrorMessage(
            error.message || "Failed to load your profile information.",
          );
          setLoading(false);
        }
        return;
      }

      if (!data) {
        if (mounted.current) {
          setErrorMessage(
            "Your profile record could not be found. Please contact support.",
          );
          setLoading(false);
        }
        return;
      }

      if (!mounted.current) return;

      setProfile(data);

      if (data.roblox_name) {
        fetchRobloxAvatar(data.roblox_name);
      } else {
        setAvatarUrl(null);
      }

      setLoading(false);
    } catch (err) {
      console.error("Unexpected error loading profile:", err);
      if (mounted.current) {
        setErrorMessage("Unexpected error while loading your profile.");
        setLoading(false);
      }
    }
  };

  const fetchRobloxAvatar = async (username) => {
    try {
      const res = await fetch("/api/robloxavatar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });

      const data = await res.json();

      if (!mounted.current) return;

      setAvatarUrl(data?.imageUrl || null);
    } catch (err) {
      console.error("Roblox avatar fetch failed", err);
      if (mounted.current) setAvatarUrl(null);
    }
  };

  const handleChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveProfile = async () => {
    if (!profile) return;

    try {
      setSaving(true);
      setErrorMessage(null);

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name,
          roblox_name: profile.roblox_name,
          discord_name: profile.discord_name,
          discord_id: profile.discord_id,
        })
        .eq("id", profile.id);

      if (error) {
        console.error("Profile update error:", error);
        setErrorMessage(error.message || "Failed to save profile.");
        setSaving(false);
        return;
      }

      if (profile.roblox_name) {
        fetchRobloxAvatar(profile.roblox_name);
      } else {
        setAvatarUrl(null);
      }

      setSaving(false);
    } catch (err) {
      console.error("Unexpected error saving profile:", err);
      setErrorMessage("Unexpected error while saving profile.");
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      setErrorMessage(null);

      const { error } = await signOutWithAudit();

      if (error) {
        console.error("Logout error:", error);
        setErrorMessage("Failed to log out. Please try again.");
        return;
      }

      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Unexpected logout error:", err);
      setErrorMessage("Unexpected error while logging out.");
    }
  };

  /* ---------------- UI ---------------- */

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <IntraHeader />
        <div className="nhsuk-width-container nhsuk-u-margin-top-6">
          Loading profile…
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <IntraHeader />

      <div className="nhsuk-width-container nhsuk-u-margin-top-6">
        <h1 className="nhsuk-heading-l">My Profile</h1>

        {errorMessage && (
          <div
            className="nhsuk-error-summary nhsuk-u-margin-top-4"
            role="alert"
            aria-live="assertive"
          >
            <h2 className="nhsuk-error-summary__title">There is a problem</h2>
            <div className="nhsuk-error-summary__body">
              <p>{errorMessage}</p>
            </div>
          </div>
        )}

        {!profile && !errorMessage && (
          <p className="nhsuk-u-margin-top-4">
            No profile information available.
          </p>
        )}

        {profile && (
          <div className="nhsuk-card nhsuk-u-margin-top-5">
            <div className="nhsuk-card__content">
              {/* Avatar */}
              <div className="flex flex-col items-center nhsuk-u-margin-bottom-5">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Roblox Avatar"
                    className="rounded-full"
                    style={{
                      width: 120,
                      height: 120,
                      border: "3px solid #005EB8",
                    }}
                  />
                ) : (
                  <FaUserCircle size={120} color="#AEB7BD" />
                )}

                <div className="nhsuk-u-margin-top-2 nhsuk-hint">
                  Profile Picture
                </div>
              </div>

              {/* Form grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label className="nhsuk-label">Full Name</label>
                  <input
                    className="nhsuk-input"
                    value={profile.full_name || ""}
                    onChange={(e) => handleChange("full_name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="nhsuk-label">Name prefix / title</label>
                  <input className="nhsuk-input" value={profile.name_prefix || ""} readOnly disabled />
                  <div className="nhsuk-hint">Managed by Management Profiles. Your displayed name is <strong>{formatProfileName(profile)}</strong>.</div>
                </div>

                {/* Roblox Name */}
                <div>
                  <label className="nhsuk-label">Roblox Username</label>
                  <input
                    className="nhsuk-input"
                    value={profile.roblox_name || ""}
                    onChange={(e) =>
                      handleChange("roblox_name", e.target.value)
                    }
                    placeholder="e.g. CJayStreamz07"
                  />
                </div>

                {/* Discord Username */}
                <div>
                  <label className="nhsuk-label">Discord Username</label>
                  <input
                    className="nhsuk-input"
                    value={profile.discord_name || ""}
                    onChange={(e) =>
                      handleChange("discord_name", e.target.value)
                    }
                    placeholder="e.g. ax1x1x1x1b"
                  />
                </div>

                {/* Discord ID */}
                <div>
                  <label className="nhsuk-label">Discord ID</label>
                  <input
                    className="nhsuk-input"
                    value={profile.discord_id || ""}
                    onChange={(e) => handleChange("discord_id", e.target.value)}
                    placeholder="e.g. 972599697229365278"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="nhsuk-label">Email</label>
                  <input
                    className="nhsuk-input"
                    value={profile.email || ""}
                    disabled
                  />
                </div>

                {/* Account Created */}
                <div>
                  <label className="nhsuk-label">Account Created</label>
                  <input
                    className="nhsuk-input"
                    value={
                      profile.created_at
                        ? new Date(profile.created_at).toLocaleString()
                        : ""
                    }
                    disabled
                  />
                </div>
              </div>

              <div className="nhsuk-u-margin-top-6 flex flex-wrap gap-4">
                <button
                  className="nhsuk-button"
                  data-module="nhsuk-button"
                  onClick={saveProfile}
                  disabled={saving}
                  type="button"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>

                {/* Warning style logout button */}
                <button
                  className="nhsuk-button nhsuk-button--warning"
                  data-module="nhsuk-button"
                  type="button"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
